import React from 'react';
import StatCard from './StatCard';
import { useDashboardStats } from '../useData';
import { useInvoices, Invoice } from '../../../hooks/useInvoices';
import { usePayables } from '../../payables/useData';
import { useClients } from '../../clients/useData';
import { useTasks } from '../../../hooks/useTasks';
import { useTodos } from '../../crm-todos/useData';
import { useProjects } from '../../crm-projects/useData';
import { usePackages } from '../../crm-packages/useData';
import { useWorkflows } from '../../client-workflows/useData';
import { useClientServices } from '../../client-services/useData';
import { useAppointments } from '../../crm-appointments/useData';
import { useLeads } from '../../crm-leads/useData';
import { useInvoicePayments } from '../../client-invoice-payments/useData';
import { useSupplierInvoices } from '../../supplier-invoices/useData';

export type MetricKey =
  | 'openTickets'
  | 'pendingInvoices'
  | 'expenses'
  | 'totalClients'
  | 'activeTasks'
  | 'revenueTotal'
  | 'pendingTodos'
  | 'overdueTasks'
  | 'activeProjects'
  | 'activeWorkflows'
  | 'unpaidInvoicesAmount'
  | 'newClientsThisMonth'
  | 'invoicePaymentsTotal'
  | 'packagesCount'
  | 'servicesExpiringCount'
  | 'openedLeadsCount'
  | 'missedAppointments'
  | 'pendingPayablesCount'
  | 'overduePayablesCount'
  | 'supplierInvoicesUnpaidCount';

const icons: Record<MetricKey, React.ReactNode> = {
  openTickets: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  pendingInvoices: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  expenses: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  totalClients: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  activeTasks: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  revenueTotal: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  pendingTodos: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  overdueTasks: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  activeProjects: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  activeWorkflows: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  unpaidInvoicesAmount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  newClientsThisMonth: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3z" />
    </svg>
  ),
  invoicePaymentsTotal: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  packagesCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  servicesExpiringCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  openedLeadsCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  missedAppointments: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  pendingPayablesCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  overduePayablesCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  supplierInvoicesUnpaidCount: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
};

const defaultTitles: Record<MetricKey, string> = {
  openTickets: 'Open Tickets',
  pendingInvoices: 'Pending Invoices',
  expenses: 'Expenses',
  totalClients: 'Total Clients',
  activeTasks: 'Active Tasks',
  revenueTotal: 'Total Revenue',
  pendingTodos: 'Pending Todos',
  overdueTasks: 'Overdue Tasks',
  activeProjects: 'Active Projects',
  activeWorkflows: 'Active Workflows',
  unpaidInvoicesAmount: 'Unpaid Invoices',
  newClientsThisMonth: 'New Clients (Month)',
  invoicePaymentsTotal: 'Payments Received',
  packagesCount: 'Packages',
  servicesExpiringCount: 'Services Expiring',
  openedLeadsCount: 'Open Leads',
  missedAppointments: 'Missed Appointments',
  pendingPayablesCount: 'Pending Payables',
  overduePayablesCount: 'Overdue Payables',
  supplierInvoicesUnpaidCount: 'Unpaid Supplier Invoices',
};

export interface StatCardWidgetProps {
  metricKey?: MetricKey;
  title?: string;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  scope?: 'myWork' | 'team';
}

const StatCardWidget: React.FC<StatCardWidgetProps> = ({
  metricKey: metricKeyProp,
  title,
  embedded,
  settings,
  scope,
}) => {
  const metricKey = (settings?.metricKey ?? metricKeyProp ?? 'openTickets') as MetricKey;
  const { stats, isLoading: statsLoading } = useDashboardStats(scope);
  const { invoices = [] } = useInvoices(undefined, 'pending');
  const { data: payablesPaid = [] } = usePayables(undefined, 'paid');
  const { total: clientsTotal, data: clients = [] } = useClients();
  const { tasks = [] } = useTasks();
  const { todos = [] } = useTodos();
  const { total: projectsTotal, isLoading: projectsLoading } = useProjects();
  const { data: workflows = [], isLoading: workflowsLoading } = useWorkflows(false);
  const { data: packages = [], isLoading: packagesLoading } = usePackages();
  const { next30DaysCount: servicesExpiring, isLoading: servicesLoading } = useClientServices();
  const { appointments = [] } = useAppointments();
  const { openedCount: openedLeads = 0 } = useLeads();
  const { invoicePayments = [] } = useInvoicePayments();
  const { data: payablesPending = [], isLoading: payablesLoading } = usePayables(undefined, 'pending');
  const { unpaidCount: supplierInvoicesUnpaid = 0 } = useSupplierInvoices();

  const invoicesArray = Array.isArray(invoices) ? invoices : [];
  const payablesPendingArray = Array.isArray(payablesPending) ? payablesPending : [];
  const payablesPaidArray = Array.isArray(payablesPaid) ? payablesPaid : [];
  const tasksArray = Array.isArray(tasks) ? tasks : [];
  const todosArray = Array.isArray(todos) ? todos : [];
  const appointmentsArray = Array.isArray(appointments) ? appointments : [];

  const pendingInvoicesTotal = invoicesArray
    .filter((inv: Invoice) => !inv.invoiceIncome || inv.invoiceIncome === 0)
    .reduce((sum: number, inv: Invoice) => sum + (inv.invoiceAmount || 0), 0);
  const totalExpenses = payablesPaidArray.reduce((sum: number, p: { amount?: number }) => sum + (p.amount || 0), 0);
  const revenueFromInvoices = invoicesArray.reduce(
    (sum: number, inv: Invoice) => sum + (inv.invoiceIncome || 0),
    0
  );
  const revenueTotal = (stats.totalRevenue ?? revenueFromInvoices) as number;
  const pendingTodosCount = todosArray.filter((t: { todoCompleted?: boolean }) => !t.todoCompleted).length;
  const today = new Date().toISOString().slice(0, 10);
  const overdueTasksCount = tasksArray.filter((t: { taskDate?: string; taskStatusDescr?: { ts_descr?: string } }) => {
    const status = t.taskStatusDescr?.ts_descr?.toLowerCase() ?? '';
    const completed = status.includes('completed') || status.includes('done');
    return t.taskDate && t.taskDate < today && !completed;
  }).length;
  const activeWorkflowsCount = workflows.filter((w: { isActive?: boolean }) => w.isActive).length;
  const thisMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const newClientsThisMonthCount = clients.filter((c: { clientCreatedAt?: string }) => {
    const created = c.clientCreatedAt?.slice(0, 10);
    return created && created >= thisMonthStart;
  }).length;
  const invoicePaymentsSum = (invoicePayments as Array<{ paymentAmount?: number }>).reduce(
    (sum, p) => sum + (p.paymentAmount || 0),
    0
  );
  const missedAppointmentsCount = appointmentsArray.filter((a: { clientAppScheduleDateTime?: string; clientAppStatusDescr?: { as_descr?: string } }) => {
    const dt = a.clientAppScheduleDateTime?.slice(0, 10);
    const status = a.clientAppStatusDescr?.as_descr?.toLowerCase() ?? '';
    const cancelled = status.includes('cancel') || status.includes('no show');
    return dt && dt < today && !cancelled;
  }).length;
  const overduePayablesCount = payablesPendingArray.filter(
    (p: { dueDate?: string }) => p.dueDate && p.dueDate < today
  ).length;
  const activePackagesCount = (Array.isArray(packages) ? packages : []).filter(
    (pkg: { isOpen?: boolean }) => pkg.isOpen === true
  ).length;

  const customTitle = typeof title === 'string' && title.trim() ? title.trim() : null;
  const displayTitle = customTitle ?? defaultTitles[metricKey];

  const values: Record<MetricKey, string | number> = {
    openTickets: stats.openTickets ?? 0,
    pendingInvoices: `€${(stats.pendingInvoices ?? pendingInvoicesTotal ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    expenses: `€${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    totalClients: stats.totalClients ?? clientsTotal ?? 0,
    activeTasks: stats.activeTasks ?? tasksArray.length ?? 0,
    revenueTotal: `€${(Number(revenueTotal) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    pendingTodos: stats.pendingTodos ?? pendingTodosCount,
    overdueTasks: stats.overdueTasks ?? overdueTasksCount,
    activeProjects: projectsTotal ?? 0,
    activeWorkflows: activeWorkflowsCount,
    unpaidInvoicesAmount: `€${(pendingInvoicesTotal ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    newClientsThisMonth: newClientsThisMonthCount,
    invoicePaymentsTotal: `€${invoicePaymentsSum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    packagesCount: activePackagesCount,
    servicesExpiringCount: servicesExpiring ?? 0,
    openedLeadsCount: openedLeads ?? 0,
    missedAppointments: missedAppointmentsCount,
    pendingPayablesCount: payablesPendingArray.length,
    overduePayablesCount,
    supplierInvoicesUnpaidCount: supplierInvoicesUnpaid ?? 0,
  };

  const changes: Record<MetricKey, string | undefined> = {
    openTickets: undefined,
    pendingInvoices: `${invoicesArray.length} pending`,
    expenses: undefined,
    totalClients: undefined,
    activeTasks: undefined,
    revenueTotal: stats.monthlyRevenue != null ? `€${Number(stats.monthlyRevenue).toLocaleString()}/mo` : undefined,
    pendingTodos: undefined,
    overdueTasks: undefined,
    activeProjects: undefined,
    activeWorkflows: undefined,
    unpaidInvoicesAmount: undefined,
    newClientsThisMonth: undefined,
    invoicePaymentsTotal: `${(invoicePayments as any[])?.length ?? 0} payments`,
    packagesCount: undefined,
    servicesExpiringCount: undefined,
    openedLeadsCount: undefined,
    missedAppointments: undefined,
    pendingPayablesCount: undefined,
    overduePayablesCount: undefined,
    supplierInvoicesUnpaidCount: undefined,
  };

  const isLoading =
    statsLoading ||
    (metricKey === 'activeProjects' && projectsLoading) ||
    (metricKey === 'activeWorkflows' && workflowsLoading) ||
    (metricKey === 'packagesCount' && packagesLoading) ||
    (metricKey === 'servicesExpiringCount' && servicesLoading) ||
    ((metricKey === 'pendingPayablesCount' || metricKey === 'overduePayablesCount') && payablesLoading);

  const changeVariants: Partial<Record<MetricKey, 'success' | 'warning' | 'neutral'>> = {
    pendingInvoices: 'neutral',
    revenueTotal: 'neutral',
    invoicePaymentsTotal: 'neutral',
  };

  return (
    <StatCard
      title={displayTitle}
      value={values[metricKey]}
      change={changes[metricKey]}
      changeVariant={changeVariants[metricKey]}
      icon={icons[metricKey]}
      isLoading={isLoading}
      embedded={embedded}
    />
  );
};

export default StatCardWidget;
